import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api, type PreviaExclusao, type UsuarioDetalhe } from '../dados/api.js'

/// Os botões que mexem no acesso de uma pessoa.
///
/// Todos confirmam antes, e a confirmação diz **o que vai acontecer de fato**, não
/// "tem certeza?". Para a exclusão isso importa especialmente: o servidor recusa
/// apagar quem tem histórico e desativa no lugar — a tela pergunta antes qual dos
/// dois vai ser, para a pessoa decidir sabendo.

export function AcoesUsuario({ usuario, souEu }: { usuario: UsuarioDetalhe; souEu: boolean }) {
  const cliente = useQueryClient()
  const [confirmando, setConfirmando] = useState<'excluir' | null>(null)
  const [motivo, setMotivo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [feito, setFeito] = useState<string | null>(null)

  function invalidar() {
    void cliente.invalidateQueries({ queryKey: ['admin'] })
  }

  const alterar = useMutation({
    mutationFn: (dados: { acessoLiberado?: boolean; ativo?: boolean }) =>
      api.adminAlterarUsuario(usuario.id, { ...dados, motivo: motivo.trim() || undefined }),
    onSuccess: () => {
      setErro(null)
      setMotivo('')
      invalidar()
    },
    onError: (e) => setErro((e as Error).message),
  })

  const previa = useQuery({
    queryKey: ['admin', 'previa-exclusao', usuario.id],
    queryFn: () => api.adminPreviaExclusao(usuario.id),
    enabled: confirmando === 'excluir',
  })

  const excluir = useMutation({
    mutationFn: () => api.adminExcluirUsuario(usuario.id, motivo.trim() || undefined),
    onSuccess: (r) => {
      setErro(null)
      setConfirmando(null)
      setMotivo('')
      setFeito(
        r.resultado === 'excluido'
          ? 'Cadastro excluído.'
          : r.jaEstavaAssim
            ? 'Este cadastro já estava desativado. Nada mudou.'
            : `Cadastro desativado. ${r.explicacao}`,
      )
      invalidar()
    },
    onError: (e) => setErro((e as Error).message),
  })

  const ocupado = alterar.isPending || excluir.isPending

  if (souEu) {
    return (
      <p className="border-t border-slate-100 px-3 py-2 text-xs text-slate-500">
        Este é o seu próprio usuário. Alterar o próprio acesso não é permitido.
      </p>
    )
  }

  return (
    <div className="space-y-2 border-t border-slate-200 p-3">
      {feito && (
        <p className="rounded bg-leve/10 px-3 py-2 text-sm text-leve" role="status">
          {feito}
        </p>
      )}
      {erro && (
        <p className="rounded bg-alta/10 px-3 py-2 text-sm text-alta" role="alert">
          {erro}
        </p>
      )}

      {confirmando === 'excluir' ? (
        <div className="rounded-lg border border-alta/30 bg-alta/5 p-3">
          {previa.isLoading && <p className="text-sm text-slate-600">Conferindo o histórico…</p>}
          {previa.data && <TextoDaExclusao previa={previa.data} nome={usuario.nome} />}

          <label className="mt-3 block">
            <span className="text-xs font-semibold text-slate-600">Motivo (fica no registro)</span>
            <input
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ex.: cadastro duplicado da migração"
              className="campo mt-1"
            />
          </label>

          <div className="mt-3 flex gap-2">
            <button
              onClick={() => excluir.mutate()}
              disabled={ocupado || previa.isLoading}
              className="botao-perigo flex-1"
            >
              {excluir.isPending
                ? 'Removendo…'
                : previa.data?.podeExcluir
                  ? 'Excluir definitivamente'
                  : 'Desativar cadastro'}
            </button>
            <button
              onClick={() => {
                setConfirmando(null)
                setMotivo('')
              }}
              className="botao-neutro flex-1"
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {usuario.acessoLiberado ? (
            <button
              onClick={() => alterar.mutate({ acessoLiberado: false })}
              disabled={ocupado}
              className="botao-neutro !min-h-0 !py-1.5 text-sm"
            >
              Tirar acesso
            </button>
          ) : (
            <button
              onClick={() => alterar.mutate({ acessoLiberado: true, ativo: true })}
              disabled={ocupado}
              className="botao-primario !min-h-0 !py-1.5 text-sm"
            >
              Liberar acesso
            </button>
          )}

          {usuario.ativo ? (
            <button
              onClick={() => alterar.mutate({ ativo: false, acessoLiberado: false })}
              disabled={ocupado}
              className="botao-neutro !min-h-0 !py-1.5 text-sm"
            >
              Desativar
            </button>
          ) : (
            <button
              onClick={() => alterar.mutate({ ativo: true })}
              disabled={ocupado}
              className="botao-neutro !min-h-0 !py-1.5 text-sm"
            >
              Reativar
            </button>
          )}

          <button
            onClick={() => setConfirmando('excluir')}
            disabled={ocupado}
            className="botao-perigo !min-h-0 !py-1.5 text-sm"
          >
            Excluir
          </button>
        </div>
      )}

      <p className="text-xs text-slate-500">
        Toda alteração aqui fica registrada na aba Auditoria, com seu nome e a data.
      </p>
    </div>
  )
}

function TextoDaExclusao({ previa, nome }: { previa: PreviaExclusao; nome: string }) {
  if (previa.podeExcluir) {
    return (
      <>
        <p className="font-semibold text-alta">Excluir {nome} definitivamente?</p>
        <p className="mt-1 text-sm text-slate-700">
          Este cadastro não tem nenhuma inspeção, revisão, não conformidade ou registro de auditoria. Pode ser
          apagado sem deixar buraco. A pessoa some das listas de encarregado e fiscal; uma nova
          carga do Power Apps a recria.
        </p>
      </>
    )
  }

  const partes = [
    previa.inspecoes && `${previa.inspecoes} ${previa.inspecoes === 1 ? 'inspeção criada' : 'inspeções criadas'}`,
    previa.revisoes && `${previa.revisoes} ${previa.revisoes === 1 ? 'revisão' : 'revisões'}`,
    previa.auditorias && `${previa.auditorias} ${previa.auditorias === 1 ? 'registro' : 'registros'} de auditoria`,
    previa.acoes && `${previa.acoes} ${previa.acoes === 1 ? 'ação administrativa' : 'ações administrativas'}`,
    previa.dispositivos &&
      `${previa.dispositivos} ${previa.dispositivos === 1 ? 'acesso registrado' : 'acessos registrados'}`,
    previa.naoConformidades &&
      `${previa.naoConformidades} ${previa.naoConformidades === 1 ? 'não conformidade' : 'não conformidades'} como responsável`,
  ].filter(Boolean) as string[]

  return (
    <>
      <p className="font-semibold text-moderada">{nome} não pode ser apagado.</p>
      <p className="mt-1 text-sm text-slate-700">
        Tem {partes.join(', ')} no sistema. Apagar o cadastro levaria junto a autoria desses
        documentos e o histórico de quando essa pessoa entrou — e uma interdição vale como prova
        justamente por ter autor.
      </p>
      <p className="mt-2 text-sm text-slate-700">
        O cadastro será <strong>desativado</strong>: a pessoa deixa de entrar e as sessões nos
        aparelhos dela são encerradas na hora, mas o nome continua nos documentos que assinou.
      </p>
    </>
  )
}
